import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import MobileTabSelect from './MobileTabSelect';
import ShowArchiveCard, { ShowArchiveCardList, cardControl, joinLocation } from './ShowArchiveCard';
import RelationshipCard, { RelationshipCardList, capacityLabel } from './RelationshipCard';
import { toggleAttended, fetchBatchEventStatus, getIdToken } from '../attendedUtils';
import { UploadModal } from './MyImages';
import DeletionRequestModal from './DeletionRequestModal';
import ActionsDropdown from './ActionsDropdown';
import FlyerHistoryTabs from './FlyerHistoryTabs';
import { ZoomHint, ImageLightbox, ImageQuickEditModal } from './ZoomableImage';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// (2026-10-15) Photos moved from the header-right info tabs to the body tabs,
// right of Flyers. Info-tab panels are matched by name, not index.
const INFO_TABS = ['Show Stats', 'Bio', 'Links', 'Projects'];
const PAGE_SIZE = 25;

// ── Parent tabs for Section 3 ─────────────────────────────────────────────────
const PARENT_TABS = ['Show Archive', 'Shows by Year', 'Festivals', 'Relationships', 'Flyers', 'Photos'];
const RELATIONSHIPS_SUBTABS = ['Artist Relationships', 'Venue Relationships'];

// ── Pagination (jump-to-page, matches FestivalDetail.js) ──────────────────────
// A direct "jump to page" control (screenshot reference: Adidas.com's site-wide
// pagination) instead of a Prev/Next + numbered-button row. A native <select>
// gives the scrollable page list and click-to-jump behavior for free.
function Pagination({ currentPage, totalItems, onPageChange, pageSize = PAGE_SIZE }) {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    if (totalPages <= 1) return null;
    return (
        <div style={styles.pageJumpWrap}>
            <span style={styles.pageJumpLabel}>Page:</span>
            <select
                value={currentPage}
                onChange={e => onPageChange(Number(e.target.value))}
                style={styles.pageJumpSelect}
            >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
            <span style={styles.pageJumpLabel}>of {totalPages}</span>
        </div>
    );
}

// ── Projects Tab (shared pattern) ─────────────────────────────────────────────
function ProjectsTab({ projects }) {
    if (!projects || projects.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ color: '#666', fontSize: '14px', marginBottom: '12px' }}>No projects yet.</div>
                <a href="/add-project" style={{ color: '#bb86fc', fontSize: '13px', textDecoration: 'none' }}>+ Start a Project</a>
            </div>
        );
    }
    return (
        <div>
            {projects.map(p => (
                <div key={p.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a2a2a' }}>
                    <div style={{ marginBottom: '6px' }}>
                        <a href={`/projects/${p.id}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: '600', fontSize: '14px' }}>{p.name}</a>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1, height: '8px', backgroundColor: '#2c2c2c', borderRadius: '4px', overflow: 'hidden', maxWidth: '200px' }}>
                            <div style={{ width: `${Math.min(p.pct_complete, 100)}%`, height: '100%', backgroundColor: '#bb86fc', borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#bb86fc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {p.pct_complete > 100 ? '100+%' : `${p.pct_complete}%`}
                        </span>
                        <span style={{ fontSize: '12px', color: '#555' }}>{p.actual_value} / {p.estimated_value} shows</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '4px' }}>{p.metric} · {p.date_start} to {p.date_end}</div>
                </div>
            ))}
        </div>
    );
}

// ── Sub-tab bar ───────────────────────────────────────────────────────────────
// Mobile (2026-10-28): a single dropdown instead of a scrolling button row.
function SubTabBar({ tabs, active, onChange }) {
    const isMobile = useIsMobile();
    if (isMobile) return <MobileTabSelect variant="sub" tabs={tabs} active={active} onChange={onChange} />;
    return (
        <div style={styles.subTabBar}>
            {tabs.map((tab, i) => (
                <button
                    key={tab}
                    onClick={() => onChange(i)}
                    style={{ ...styles.subTabBtn, ...(active === i ? styles.subTabBtnActive : {}) }}
                >
                    {tab}
                </button>
            ))}
        </div>
    );
}

// ── Photos Tab ────────────────────────────────────────────────────────────────
function PhotosTab({ promoterId, promoterName, defaultImageId, onDefaultChanged }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [settingDefault, setSettingDefault] = useState(null);
    const [lightbox, setLightbox] = useState(null);
    const [hoveredThumb, setHoveredThumb] = useState(null);
    const [editingImage, setEditingImage] = useState(null);
    const [showUpload, setShowUpload] = useState(false);
    const [removingDefault, setRemovingDefault] = useState(false);

    const loadPhotos = useCallback(async () => {
        if (!promoterId) return;
        setLoading(true);
        try {
            const token = await getIdToken();
            const res = await axios.get(`${config.API_URL}/images`, {
                params: { entityType: 'promoter', entityId: promoterId },
                headers: { Authorization: token },
            });
            const imgs = res.data.images || [];
            setImages(imgs);
            setError('');
            if (defaultImageId) {
                const defaultImg = imgs.find(i => i.id === defaultImageId);
                if (defaultImg) onDefaultChanged(defaultImg.id, defaultImg.image_url);
            }
        } catch {
            setError('Failed to load photos.');
        } finally {
            setLoading(false);
        }
    }, [promoterId, defaultImageId, onDefaultChanged]);

    useEffect(() => { loadPhotos(); }, [loadPhotos]);

    const handleUploaded = (img) => {
        setImages(prev => [img, ...prev]);
        setShowUpload(false);
        // Backend may have auto-set this upload as the promoter's default —
        // sync that up to the parent immediately rather than waiting on a
        // refetch (createImage.js reports this via becameDefault).
        if (img.becameDefault) {
            onDefaultChanged(img.id, img.image_url);
        }
    };

    const handleRemoveDefault = async () => {
        setRemovingDefault(true);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: null, entityType: 'promoter', entityId: promoterId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(null, null);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to remove default image.');
        } finally {
            setRemovingDefault(false);
        }
    };

    const handleSetDefault = async (image) => {
        setSettingDefault(image.id);
        try {
            const token = await getIdToken();
            await axios.post(
                `${config.API_URL}/images/set-default`,
                { imageId: image.id, entityType: 'promoter', entityId: promoterId },
                { headers: { Authorization: token } }
            );
            onDefaultChanged(image.id, image.image_url);
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to set default image.');
        } finally {
            setSettingDefault(null);
        }
    };

    const handleImageSaved = (updated) => {
        setImages(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i));
        setLightbox(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
    };

    const handleImageDeleted = (id) => {
        setImages(prev => prev.filter(i => i.id !== id));
        setLightbox(null); // image no longer exists — close the lightbox behind it
    };

    if (loading) return <div style={styles.emptyTab}>Loading photos…</div>;
    if (error) return <div style={styles.emptyTab}>{error}</div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                    {defaultImageId && (
                        <button
                            onClick={handleRemoveDefault}
                            disabled={removingDefault}
                            style={{ background: 'transparent', border: '1px solid #555', borderRadius: '6px', color: '#aaa', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', opacity: removingDefault ? 0.6 : 1 }}
                        >
                            {removingDefault ? 'Removing…' : '✕ Remove Default'}
                        </button>
                    )}
                </div>
                <button onClick={() => setShowUpload(true)} style={styles.addPhotoBtn}>
                    + Add Photo
                </button>
            </div>

            {images.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>No photos yet for this promoter.</div>
                </div>
            ) : (
                <div style={styles.photoGrid}>
                    {images.map(img => {
                        const isDefault = img.id === defaultImageId;
                        return (
                            <div key={img.id} style={{ ...styles.photoCard, ...(isDefault ? styles.photoCardDefault : {}) }}>
                                <div style={styles.photoThumb} onClick={() => setLightbox(img)} onMouseEnter={() => setHoveredThumb(img.id)} onMouseLeave={() => setHoveredThumb(null)}>
                                    <img src={img.image_url} alt={img.caption || promoterName} style={styles.photoThumbImg} loading="lazy" />
                                    {isDefault && <span style={styles.defaultBadge}>★ Default</span>}
                                    {!img.is_public && <span style={styles.privateBadge}>🔒</span>}
                                    <ZoomHint visible={hoveredThumb === img.id} />
                                </div>
                                <div style={styles.photoCardBody}>
                                    {img.caption && <div style={styles.photoCaption}>{img.caption}</div>}
                                    {!isDefault && img.is_public && (
                                        <button onClick={() => handleSetDefault(img)} disabled={settingDefault === img.id} style={styles.setDefaultBtn}>
                                            {settingDefault === img.id ? 'Setting…' : '★ Set as Default'}
                                        </button>
                                    )}
                                    {isDefault && <div style={styles.isDefaultLabel}>Current default photo</div>}
                                    {!img.is_public && <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Private — only visible to you</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {lightbox && (
                <ImageLightbox
                    src={lightbox.image_url}
                    alt={lightbox.caption || ''}
                    caption={lightbox.caption}
                    onClose={() => setLightbox(null)}
                    onEdit={() => setEditingImage(lightbox)}
                />
            )}

            {editingImage && (
                <ImageQuickEditModal
                    image={editingImage}
                    onClose={() => setEditingImage(null)}
                    onSaved={handleImageSaved}
                    onDeleted={handleImageDeleted}
                />
            )}

            {showUpload && (
                <UploadModal
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                    presetEntityType="promoter"
                    presetEntityId={promoterId}
                    presetEntityName={promoterName}
                />
            )}
        </div>
    );
}

// ── Show Archive Tab ──────────────────────────────────────────────────────────
function ShowArchiveTab({ shows, attendedMap, performedMap, workedMap, onToggleAttended, togglingMap }) {
    const isMobile = useIsMobile();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No events linked to this promoter yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? shows.filter(show => {
        const dateStr = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';
        return [dateStr, show.venue_name, show.venue_city, show.venue_state, show.venue_country, show.festival_name, show.artists].some(val => val && normalizeStr(val).includes(q));
    }) : shows;
    const totalShows = shows.length;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by date, venue, city, state, country, artists..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No events match your search.</div> : (
                <>
                    {isMobile ? (
                        <ShowArchiveCardList>
                            {paginated.map(show => {
                                const showNum = totalShows - shows.indexOf(show);
                                const isAttended = attendedMap[show.event_id] === true;
                                const isPerformed = performedMap[show.event_id] === true;
                                const isWorked = workedMap[show.event_id] === true;
                                const isToggling = togglingMap[show.event_id];
                                return (
                                    <ShowArchiveCard
                                        key={show.event_id}
                                        title={`Show #${showNum}`}
                                        titleTo={`/events/${show.event_slug || show.event_id}`}
                                        date={show.event_date}
                                        venueName={show.venue_name}
                                        venueTo={`/venues/${show.venue_slug || show.venue_id}`}
                                        location={joinLocation(show.venue_city, show.venue_state, show.venue_country)}
                                        festivalName={show.festival_name}
                                        festivalTo={show.festival_slug ? `/festivals/${show.festival_slug}` : null}
                                        lineup={show.artists}
                                        footer={isPerformed ? <span style={{ ...styles.performedBadge, ...cardControl }}>Performed</span>
                                        : isWorked ? <span style={{ ...styles.workedBadge, ...cardControl }}>Worked</span>
                                        : <button onClick={() => onToggleAttended(show.event_id)} disabled={isToggling} style={{ ...(isAttended ? styles.attendedBtnOn : styles.attendedBtnOff), ...cardControl }}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                    />
                                );
                            })}
                        </ShowArchiveCardList>
                    ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>#</th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Venue</th>
                                    <th style={styles.th}>City</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Country</th>
                                    <th style={styles.th}>Artists</th>
                                    <th style={styles.th}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((show, idx) => {
                                    // Reverse walk of the full display-ordered array (not the
                                    // filtered/paginated slice) so show numbers stay stable
                                    // across pages and searches — same fix as the Show History
                                    // numbering gotcha already applied on ArtistDetail.js.
                                    const globalIdx = shows.indexOf(show);
                                    const showNum = totalShows - globalIdx;
                                    const formattedDate = show.event_date ? new Date(show.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—';
                                    const isAttended = attendedMap[show.event_id] === true;
                                    const isPerformed = performedMap[show.event_id] === true;
                                    const isWorked = workedMap[show.event_id] === true;
                                    const isToggling = togglingMap[show.event_id];
                                    return (
                                        <tr key={show.event_id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                                <Link to={`/events/${show.event_slug || show.event_id}`} style={styles.tableLink}>{showNum}</Link>
                                            </td>
                                            <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>{formattedDate}</td>
                                            <td style={styles.td}>
                                                <Link to={`/venues/${show.venue_slug || show.venue_id}`} style={styles.tableLink}>{show.venue_name}</Link>
                                            </td>
                                            <td style={styles.td}>{show.venue_city || '—'}</td>
                                            <td style={styles.td}>{show.venue_state || '—'}</td>
                                            <td style={styles.td}>{show.venue_country || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                                {show.festival_name && show.festival_slug
                                                    ? <><Link to={`/festivals/${show.festival_slug}`} style={{ color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' }}>{show.festival_name}</Link>{show.artists ? ` - ${show.artists}` : ''}</>
                                                    : show.artists || '—'}
                                            </td>
                                            <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {isPerformed ? <span style={styles.performedBadge}>Performed</span>
                                                    : isWorked ? <span style={styles.workedBadge}>Worked</span>
                                                    : <button onClick={() => onToggleAttended(show.event_id)} disabled={isToggling} style={isAttended ? styles.attendedBtnOn : styles.attendedBtnOff}>{isToggling ? '…' : isAttended ? '✓ Attended' : 'Attended?'}</button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Shows by Year Tab ─────────────────────────────────────────────────────────
function ShowsByYearTab({ shows }) {
    if (!shows || shows.length === 0) return <div style={styles.emptyTab}>No shows recorded yet.</div>;
    const years = shows.map(s => new Date(s.event_date).getUTCFullYear());
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    const counts = {};
    years.forEach(y => { counts[y] = (counts[y] || 0) + 1; });
    const data = [];
    for (let y = minYear; y <= maxYear; y++) data.push({ year: y, count: counts[y] || 0 });
    const maxCount = Math.max(...data.map(d => d.count), 1);
    const barMaxHeight = 200;
    const gridLines = [];
    const step = maxCount <= 5 ? 1 : maxCount <= 10 ? 2 : maxCount <= 20 ? 5 : 10;
    for (let v = 0; v <= maxCount; v += step) gridLines.push(v);
    if (gridLines[gridLines.length - 1] < maxCount) gridLines.push(maxCount);
    const chartWidth = Math.max(data.length * 52, 400);
    const yAxisWidth = 36;
    const chartPadTop = 16;
    const chartPadBottom = 40;
    const totalHeight = barMaxHeight + chartPadTop + chartPadBottom;
    return (
        <div>
            <div style={{ overflowX: 'auto', paddingBottom: '8px' }}>
                <div style={{ display: 'flex', minWidth: chartWidth + yAxisWidth + 'px' }}>
                    <div style={{ width: yAxisWidth + 'px', flexShrink: 0, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', right: '6px', transform: 'translateY(-50%)', fontSize: '11px', color: '#666', whiteSpace: 'nowrap' }}>{v}</div>; })}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: totalHeight + 'px' }}>
                        {gridLines.map(v => { const top = chartPadTop + barMaxHeight - (v / maxCount) * barMaxHeight; return <div key={v} style={{ position: 'absolute', top: top + 'px', left: 0, right: 0, borderTop: `1px solid ${v === 0 ? '#444' : '#2a2a2a'}`, zIndex: 0 }} />; })}
                        <div style={{ display: 'flex', alignItems: 'flex-end', height: barMaxHeight + 'px', marginTop: chartPadTop + 'px', gap: '4px', paddingLeft: '8px', paddingRight: '8px', position: 'relative', zIndex: 1 }}>
                            {data.map(({ year, count }) => {
                                const barH = count === 0 ? 0 : Math.max((count / maxCount) * barMaxHeight, 4);
                                return (
                                    <div key={year} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }} title={`${year}: ${count} show${count !== 1 ? 's' : ''}`}>
                                        {count > 0 && <div style={{ fontSize: '11px', color: '#bb86fc', fontWeight: 'bold', opacity: 0.85 }}>{count}</div>}
                                        <div style={{ width: '100%', maxWidth: '44px', height: barH + 'px', backgroundColor: count === 0 ? 'transparent' : '#bb86fc', borderRadius: '3px 3px 0 0' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', paddingLeft: '8px', paddingRight: '8px', gap: '4px', marginTop: '6px' }}>
                            {data.map(({ year, count }) => <div key={year} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: count === 0 ? '#444' : '#888', whiteSpace: 'nowrap', overflow: 'hidden' }}>{year}</div>)}
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '32px', fontSize: '13px', color: '#888' }}>
                <span>Total Years Active: <strong style={{ color: '#e0e0e0' }}>{data.filter(d => d.count > 0).length}</strong></span>
                <span>Peak Year: <strong style={{ color: '#bb86fc' }}>{data.reduce((best, d) => d.count > best.count ? d : best, data[0]).year} ({data.reduce((best, d) => d.count > best.count ? d : best, data[0]).count} shows)</strong></span>
            </div>
        </div>
    );
}

// ── Festivals Tab (unchanged from prior PromoterDetail.js, repositioned) ──────
function FestivalsTab({ festivals, festivalEvents }) {
    const [search, setSearch] = useState('');
    const [expandedFestival, setExpandedFestival] = useState(null);
    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };
    const q = normalizeStr(search);
    const filteredFestivals = search
        ? (festivals || []).filter(f => [
            f.festival_name,
            f.start_date ? formatDate(f.start_date) : '',
            f.headliners
          ].some(v => v && normalizeStr(v).includes(q)))
        : (festivals || []);

    if (!festivals || festivals.length === 0) return <div style={styles.emptyTab}>No festivals linked to this promoter yet.</div>;

    return (
        <div>
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={styles.tabSearch} />
            {filteredFestivals.length === 0 ? <div style={styles.emptyTab}>No festivals match your search.</div> : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}></th>
                                <th style={styles.th}>Festival</th>
                                <th style={styles.th}>Start Date</th>
                                <th style={styles.th}>End Date</th>
                                <th style={styles.th}>Headliners</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredFestivals.map((f, idx) => {
                                const isExpanded = expandedFestival === f.festival_id;
                                const festEvents = (festivalEvents || []).filter(e => e.festival_id === f.festival_id);
                                return (
                                    <React.Fragment key={f.festival_id}>
                                        <tr
                                            style={{ ...(idx % 2 === 0 ? styles.trEven : styles.trOdd), cursor: festEvents.length > 0 ? 'pointer' : 'default' }}
                                            onClick={() => festEvents.length > 0 && setExpandedFestival(isExpanded ? null : f.festival_id)}
                                        >
                                            <td style={{ ...styles.td, width: '28px', color: '#bb86fc', fontSize: '12px' }}>
                                                {festEvents.length > 0 ? (isExpanded ? '▾' : '▸') : ''}
                                            </td>
                                            <td style={styles.td}>
                                                <Link to={`/festivals/${f.festival_slug || f.festival_id}`} style={styles.tableLink} onClick={e => e.stopPropagation()}>
                                                    {f.festival_name}
                                                </Link>
                                            </td>
                                            <td style={styles.td}>{formatDate(f.start_date)}</td>
                                            <td style={styles.td}>{formatDate(f.end_date)}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{f.headliners || '—'}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={5} style={{ padding: '0', backgroundColor: '#131313', borderBottom: '1px solid #333' }}>
                                                    <table style={{ ...styles.table, margin: 0 }}>
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#1a1a1a' }}>
                                                                <th style={{ ...styles.th, paddingLeft: '40px' }}>Date</th>
                                                                <th style={styles.th}>City</th>
                                                                <th style={styles.th}>State</th>
                                                                <th style={styles.th}>Country</th>
                                                                <th style={styles.th}>Venue</th>
                                                                <th style={styles.th}>Artists</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {festEvents.map((e, ei) => (
                                                                <tr key={e.event_id} style={{ backgroundColor: ei % 2 === 0 ? '#161616' : '#131313' }}>
                                                                    <td style={{ ...styles.td, paddingLeft: '40px' }}>
                                                                        <Link to={`/events/${e.event_slug || e.event_id}`} style={styles.tableLink}>
                                                                            {formatDate(e.event_date)}
                                                                        </Link>
                                                                    </td>
                                                                    <td style={styles.td}>{e.venue_city || '—'}</td>
                                                                    <td style={styles.td}>{e.venue_state || '—'}</td>
                                                                    <td style={styles.td}>{e.venue_country || '—'}</td>
                                                                    <td style={styles.td}>
                                                                        <Link to={`/venues/${e.venue_slug || e.venue_id}`} style={styles.tableLink}>
                                                                            {e.venue_name}
                                                                        </Link>
                                                                    </td>
                                                                    <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{e.artists || '—'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Artist Relationships Tab ──────────────────────────────────────────────────
// Column set mirrors VenueDetail.js's Artists Hosted tab (Artist Type/Style
// added alongside the base ArtistDetail.js-style Artist Relationships columns),
// per Andy's 2026-09-11 request.
function ArtistRelationshipsTab({ artistRelationships }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    if (!artistRelationships || artistRelationships.length === 0) return <div style={styles.emptyTab}>No artist relationships recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? artistRelationships.filter(a => [a.name, a.artist_type, a.artist_style, a.city, a.state, a.country].some(val => val && normalizeStr(val).includes(q))) : artistRelationships;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by artist, type, style, city, state, country..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No artists match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(a => (
                                <RelationshipCard
                                    key={a.id}
                                    title={a.name}
                                    titleTo={`/artists/${a.slug || a.id}`}
                                    lines={[
                                        [a.artist_type, a.artist_style].filter(Boolean).join(' · '),
                                        joinLocation(a.city, a.state, a.country),
                                    ]}
                                    counts={[{ value: `${a.times_played_together}x`, label: 'Shows Together' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Artist</th>
                                        <th style={styles.th}>Artist Type</th>
                                        <th style={styles.th}>Artist Style</th>
                                        <th style={styles.th}>City</th>
                                        <th style={styles.th}>State/Region</th>
                                        <th style={styles.th}>Country</th>
                                        <th style={styles.th}>Shows Together</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((a, idx) => (
                                        <tr key={a.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/artists/${a.slug || a.id}`} style={styles.tableLink}>{a.name}</Link></td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{a.artist_type || '—'}</td>
                                            <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>{a.artist_style || '—'}</td>
                                            <td style={styles.td}>{a.city || '—'}</td>
                                            <td style={styles.td}>{a.state || '—'}</td>
                                            <td style={styles.td}>{a.country || '—'}</td>
                                            <td style={styles.td}><span style={styles.showCount}>{a.times_played_together}x</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Venue Relationships Tab ────────────────────────────────────────────────────
function VenueRelationshipsTab({ venueRelationships }) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const isMobile = useIsMobile();
    if (!venueRelationships || venueRelationships.length === 0) return <div style={styles.emptyTab}>No venue relationships recorded yet.</div>;
    const q = normalizeStr(search);
    const filtered = search ? venueRelationships.filter(v => [v.name, v.city, v.state, v.country, v.capacity != null ? String(v.capacity) : ''].some(val => val && normalizeStr(val).includes(q))) : venueRelationships;
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    return (
        <div>
            <input type="text" placeholder="Search by venue, city, state, country, capacity..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={styles.tabSearch} />
            {filtered.length === 0 ? <div style={styles.emptyTab}>No venues match your search.</div> : (
                <>
                    {/* Mobile (2026-10-28): one card per row instead of the wide table */}
                    {isMobile ? (
                        <RelationshipCardList>
                            {paginated.map(venue => (
                                <RelationshipCard
                                    key={venue.id}
                                    title={venue.name}
                                    titleTo={`/venues/${venue.slug || venue.id}`}
                                    titleMeta={capacityLabel(venue.capacity)}
                                    lines={[joinLocation(venue.city, venue.state, venue.country)]}
                                    counts={[{ value: venue.times_played, label: 'Shows' }]}
                                />
                            ))}
                        </RelationshipCardList>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead><tr><th style={styles.th}>Venue</th><th style={styles.th}>City</th><th style={styles.th}>State/Region</th><th style={styles.th}>Country</th><th style={styles.th}>Capacity</th><th style={styles.th}>Shows</th></tr></thead>
                                <tbody>
                                    {paginated.map((venue, idx) => (
                                        <tr key={venue.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                            <td style={styles.td}><Link to={`/venues/${venue.slug || venue.id}`} style={styles.tableLink}>{venue.name}</Link></td>
                                            <td style={styles.td}>{venue.city || '—'}</td>
                                            <td style={styles.td}>{venue.state || '—'}</td>
                                            <td style={styles.td}>{venue.country || '—'}</td>
                                            <td style={styles.td}>{venue.capacity ? venue.capacity.toLocaleString() : '—'}</td>
                                            <td style={styles.td}><span style={styles.showCount}>{venue.times_played}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <Pagination currentPage={page} totalItems={filtered.length} onPageChange={setPage} />
                </>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
function PromoterDetail({ slugOverride } = {}) {
    const isMobile = useIsMobile();
    const params = useParams();
    const navigate = useNavigate();
    const slug = slugOverride || params.slug;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeInfoTab, setActiveInfoTab] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Section 3 parent tab + sub-tab state
    const [activeParentTab, setActiveParentTab] = useState(0);
    const [activeRelationshipsSubTab, setActiveRelationshipsSubTab] = useState(0);

    const [defaultImageId, setDefaultImageId] = useState(null);
    const [defaultImageUrl, setDefaultImageUrl] = useState(null);
    const [defaultImageResolving, setDefaultImageResolving] = useState(false);

    // Attended / performed state
    const [attendedMap, setAttendedMap] = useState({});
    const [performedMap, setPerformedMap] = useState({});
    const [workedMap, setWorkedMap] = useState({});
    const [togglingMap, setTogglingMap] = useState({});

    const loadAttendedStatus = useCallback(async (eventList) => {
        if (!eventList || eventList.length === 0) return;
        try {
            const eventIds = eventList.map(e => e.event_id);
            const statusMap = await fetchBatchEventStatus(eventIds);
            const aMap = {};
            const pMap = {};
            const wMap = {};
            Object.entries(statusMap).forEach(([id, status]) => {
                aMap[id] = status.attended;
                pMap[id] = status.performed || false;
                wMap[id] = status.worked || false;
            });
            setAttendedMap(aMap);
            setPerformedMap(pMap);
            setWorkedMap(wMap);
        } catch (e) { /* not logged in */ }
    }, []);

    const handleToggleAttended = useCallback(async (eventId) => {
        setTogglingMap(prev => ({ ...prev, [eventId]: true }));
        try {
            const result = await toggleAttended(eventId);
            setAttendedMap(prev => ({ ...prev, [eventId]: result.attended }));
            setPerformedMap(prev => ({ ...prev, [eventId]: result.performed || false }));
            setWorkedMap(prev => ({ ...prev, [eventId]: result.worked || false }));
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setTogglingMap(prev => ({ ...prev, [eventId]: false }));
        }
    }, []);

    useEffect(() => {
        async function load() {
            setDefaultImageId(null);
            setDefaultImageUrl(null);
            setDefaultImageResolving(false);
            try {
                const res = await axios.get(`${config.API_URL}/promoters/${slug}`);
                setData(res.data);
                setLoading(false);
                if (res.data?.promoter?.default_image_id) {
                    setDefaultImageId(res.data.promoter.default_image_id);
                    setDefaultImageResolving(true);
                    try {
                        const token = await getIdToken();
                        const imgRes = await axios.get(`${config.API_URL}/images`, {
                            params: { entityType: 'promoter', entityId: res.data.promoter.id },
                            headers: { Authorization: token },
                        });
                        const imgs = imgRes.data.images || [];
                        const defaultImg = imgs.find(i => i.id === res.data.promoter.default_image_id);
                        if (defaultImg) setDefaultImageUrl(defaultImg.image_url);
                    } catch { /* fall back to promoter.image_url */ }
                    finally { setDefaultImageResolving(false); }
                }
                if (res.data && res.data.promoter) {
                    document.title = `SHARC - ${res.data.promoter.name}`;
                }
                if (res.data && res.data.events) {
                    loadAttendedStatus(res.data.events);
                }
            } catch {
                setError('Failed to load promoter');
                setLoading(false);
            }
        }
        load();
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, [slug, loadAttendedStatus]);

    const handleDefaultChanged = useCallback((imageId, imageUrl) => {
        setDefaultImageId(imageId);
        setDefaultImageUrl(imageUrl);
    }, []);

    if (loading) return <div style={styles.message}>Loading promoter...</div>;
    if (error) return <div style={styles.error}>{error}</div>;
    if (!data) return null;

    const { promoter, stats, links, events, festivals, festival_events, artist_relationships, venue_relationships, projects, has_existing_events, pending_deletion } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
    };

    const formatRequestedAt = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const infoTab = INFO_TABS[activeInfoTab] || INFO_TABS[0];
    const heroImage = defaultImageUrl || promoter.image_url;
    const location = [promoter.city, promoter.state, promoter.country].filter(Boolean).join(', ');

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            {/* ── Section 1: Promoter Name + Actions dropdown ── */}
            <div style={styles.section1}>
                <h1 style={styles.promoterNameHeading}>{promoter.name}</h1>
                <div style={styles.section1Right}>
                    {!pending_deletion && (
                        <ActionsDropdown
                            editPath={`/promoters/${promoter.slug || promoter.id}/edit`}
                            editLabel="Edit Promoter"
                            onDeleteClick={() => setShowDeleteModal(true)}
                            deleteLabel="Delete Promoter"
                            deleteDisabled={has_existing_events}
                            deleteTooltip="This promoter cannot be deleted while it is tied to an existing event. Remove it from all events first."
                        />
                    )}
                </div>
            </div>

            {pending_deletion && (
                <div style={styles.pendingDeletionBanner}>
                    <span style={styles.pendingDeletionIcon}>🗑️</span>
                    <span>
                        Promoter marked for deletion by <strong>{pending_deletion.requested_by}</strong> on{' '}
                        <strong>{formatRequestedAt(pending_deletion.requested_at)}</strong>
                        {pending_deletion.reason && (
                            <> — <em>{pending_deletion.reason}</em></>
                        )}
                    </span>
                </div>
            )}

            {showDeleteModal && (
                <DeletionRequestModal
                    entityType="promoter"
                    entityId={promoter.id}
                    entityName={promoter.name}
                    onClose={() => setShowDeleteModal(false)}
                    onSuccess={() => navigate('/promoters')}
                />
            )}

            {/* ── Section 2: Image/meta left + info tabs right ── */}
            <div style={isMobile ? { ...styles.headerGrid, ...Smobile.headerGrid } : styles.headerGrid}>
                {/* Left: image + location */}
                <div style={styles.headerLeft}>
                    <div style={styles.headerImageRow}>
                        {!defaultImageResolving && heroImage ? (
                            <img
                                src={heroImage}
                                alt={promoter.name}
                                style={styles.promoterImage}
                                onError={e => { e.target.outerHTML = `<div style="width:160px;height:160px;border-radius:8px;border:1px solid #333;background:#1e1e1e;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;flex-shrink:0"><div style="font-size:11px;color:#555;text-transform:uppercase;letter-spacing:0.5px">No Image</div></div>`; }}
                            />
                        ) : (
                            <div style={styles.noImageBox}>
                                <div style={styles.noImageLabel}>{defaultImageResolving ? '…' : 'No Image'}</div>
                            </div>
                        )}
                        <div style={styles.headerMeta}>
                            {location && <div style={styles.location}>{location}</div>}
                        </div>
                    </div>
                </div>

                {/* Right: info tabs */}
                <div style={styles.headerRight}>
                    {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                    {isMobile ? (
                        <MobileTabSelect tabs={INFO_TABS} active={activeInfoTab} onChange={setActiveInfoTab} />
                    ) : (
                        <div style={styles.infoTabBar}>
                            {INFO_TABS.map((tab, i) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveInfoTab(i)}
                                    style={{ ...styles.infoTabBtn, ...(activeInfoTab === i ? styles.infoTabBtnActive : {}) }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    )}
                    <div style={styles.infoTabContent}>
                        {/* Show Stats */}
                        {infoTab === 'Show Stats' && (() => {
                            let termStr = '—';
                            if (stats.first_show_date && stats.last_show_date) {
                                let d1 = new Date(stats.first_show_date); let d2 = new Date(stats.last_show_date);
                                let years = d2.getUTCFullYear() - d1.getUTCFullYear(); let months = d2.getUTCMonth() - d1.getUTCMonth(); let days = d2.getUTCDate() - d1.getUTCDate();
                                if (days < 0) { months -= 1; days += new Date(d2.getUTCFullYear(), d2.getUTCMonth(), 0).getDate(); }
                                if (months < 0) { years -= 1; months += 12; }
                                const parts = [];
                                if (years > 0) parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
                                if (months > 0) parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
                                if (days > 0) parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
                                termStr = parts.join(' ') || '—';
                            }
                            return (
                                <div style={isMobile ? { ...styles.showStatsLayout, ...Smobile.showStatsLayout } : styles.showStatsLayout}>
                                    <div style={styles.totalShowsBox}>
                                        <div style={styles.statValue}>{stats.total_events || 0}</div>
                                        <div style={styles.statLabel}>Total Shows</div>
                                    </div>
                                    <table style={styles.statsTable}>
                                        <tbody>
                                            <tr>
                                                <td style={styles.statsTableLabel}>First Show</td>
                                                <td style={styles.statsTableValue}>{formatDate(stats.first_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Most Recent Show</td>
                                                <td style={styles.statsTableValue}>{formatDate(stats.last_show_date)}</td>
                                            </tr>
                                            <tr>
                                                <td style={styles.statsTableLabel}>Term of Shows</td>
                                                <td style={styles.statsTableValue}>{termStr}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}

                        {/* Bio */}
                        {infoTab === 'Bio' && (
                            <div>
                                {promoter.description
                                    ? <div style={styles.bioText}>{promoter.description}</div>
                                    : <div style={styles.emptyInfo}>No bio available.</div>}
                            </div>
                        )}

                        {/* Links */}
                        {infoTab === 'Links' && (
                            <div>
                                {links && links.length > 0
                                    ? <div style={styles.linksGrid}>
                                        {links.map(link => (
                                            <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" style={styles.linkChip}>
                                                {link.description || link.url} ↗
                                            </a>
                                        ))}
                                    </div>
                                    : <div style={styles.emptyInfo}>No links added yet.</div>}
                            </div>
                        )}


                        {/* Projects */}
                        {infoTab === 'Projects' && (
                            <ProjectsTab projects={projects} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section 3: Multi-level tabs ── */}
            <div style={styles.tabsWrapper}>
                {/* Parent tab bar */}
                {/* Mobile (2026-10-28): a single dropdown instead of a scrolling tab row */}
                {isMobile ? (
                    <MobileTabSelect tabs={PARENT_TABS} active={activeParentTab} onChange={setActiveParentTab} />
                ) : (
                    <div style={styles.tabBar}>
                        {PARENT_TABS.map((tab, i) => (
                            <button key={tab} onClick={() => setActiveParentTab(i)} style={{ ...styles.tabBtn, ...(activeParentTab === i ? styles.tabBtnActive : {}) }}>{tab}</button>
                        ))}
                    </div>
                )}

                <div style={styles.tabContent}>
                    {/* ── Show Archive ── */}
                    {activeParentTab === 0 && (
                        <ShowArchiveTab
                            shows={events}
                            attendedMap={attendedMap}
                            performedMap={performedMap}
                            workedMap={workedMap}
                            onToggleAttended={handleToggleAttended}
                            togglingMap={togglingMap}
                        />
                    )}

                    {/* ── Shows by Year ── */}
                    {activeParentTab === 1 && <ShowsByYearTab shows={events} />}

                    {/* ── Festivals ── */}
                    {activeParentTab === 2 && <FestivalsTab festivals={festivals} festivalEvents={festival_events} />}

                    {/* ── Relationships ── */}
                    {activeParentTab === 3 && (
                        <div>
                            <SubTabBar tabs={RELATIONSHIPS_SUBTABS} active={activeRelationshipsSubTab} onChange={setActiveRelationshipsSubTab} />
                            <div style={styles.subTabContent}>
                                {activeRelationshipsSubTab === 0 && <ArtistRelationshipsTab artistRelationships={artist_relationships} />}
                                {activeRelationshipsSubTab === 1 && <VenueRelationshipsTab venueRelationships={venue_relationships} />}
                            </div>
                        </div>
                    )}

                    {/* ── Flyers ── */}
                    {activeParentTab === 4 && (
                        <FlyerHistoryTabs
                            entityType="promoter"
                            entityId={promoter.id}
                            shows={events}
                            searchByOptions={['Artist', 'Festival', 'Venue']}
                            multiEventTabLabel="Calendar Flyer History"
                            showFlyerHistoryLabel="Show Flyer History"
                        />
                    )}

                    {/* ── Photos (moved from the info tabs, 2026-10-15) ── */}
                    {activeParentTab === 5 && (
                        <PhotosTab
                            promoterId={promoter.id}
                            promoterName={promoter.name}
                            defaultImageId={defaultImageId}
                            onDefaultChanged={handleDefaultChanged}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    // ── Section 1 ──
    section1: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    section1Right: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
    promoterNameHeading: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0, lineHeight: 1.2 },
    pendingDeletionBanner: {
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        backgroundColor: '#2c1818', border: '1px solid #e57373', borderRadius: '6px',
        padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#e0a0a0', lineHeight: 1.5
    },
    pendingDeletionIcon: { fontSize: '16px', lineHeight: 1.4, flexShrink: 0 },

    // ── Section 2 header grid ──
    headerGrid: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px', marginBottom: '32px', alignItems: 'start' },
    headerLeft: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
    headerImageRow: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    promoterImage: { width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #333', flexShrink: 0 },
    noImageBox: { width: '160px', height: '160px', borderRadius: '8px', border: '1px solid #333', background: '#1e1e1e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', flexShrink: 0 },
    noImageLabel: { fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
    headerMeta: { display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: '4px' },
    location: { fontSize: '15px', color: '#aaa', marginBottom: '6px' },

    // ── Info tabs (right side of section 2) ──
    // minWidth: 0 is the actual fix here — without it, this grid column
    // refuses to shrink below infoTabBar's content width, forcing the whole
    // page wider than the viewport on mobile instead of letting
    // infoTabBar's own overflowX: 'auto' scroll internally as intended.
    headerRight: { minWidth: 0 },
    // Tab bars (2026-09-23 scroll fix, same as MyShows.js): overflowX: 'auto'
    // forces overflow-y to 'auto' too, and the old negative-marginBottom tab
    // buttons overflowed the bar by 1–2px, so a mouse wheel over the bar
    // scrolled the bar itself instead of the page. Divider is now an inset
    // box-shadow (painted beneath the buttons, so the active underline covers
    // it with zero overflow) plus an explicit overflowY: 'hidden'.
    infoTabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', gap: '2px', overflowX: 'auto', overflowY: 'hidden' },
    infoTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '10px 16px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap' },
    infoTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    infoTabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '16px', minHeight: '160px', maxHeight: '280px', overflowY: 'auto' },
    bioText: { fontSize: '14px', color: '#ccc', lineHeight: '1.7', maxHeight: '96px', overflowY: 'auto' },

    // ── Show Stats tab ──
    showStatsLayout: { display: 'flex', gap: '24px', alignItems: 'center', height: '100%' },
    totalShowsBox: { flexShrink: 0, backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '20px 28px', textAlign: 'center', minWidth: '110px' },
    statsTable: { flex: 1, borderCollapse: 'collapse', fontSize: '14px' },
    statsTableLabel: { padding: '10px 16px 10px 0', color: '#aaa', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', width: '1%' },
    statsTableValue: { padding: '10px 0', color: '#e0e0e0', fontWeight: 'bold', fontSize: '15px', borderBottom: '1px solid #2a2a2a' },
    linksGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    linkChip: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 14px', fontSize: '13px', color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    emptyInfo: { color: '#555', fontSize: '14px', padding: '20px 0', textAlign: 'center' },

    // ── Photos tab ──
    photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' },
    photoCard: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', overflow: 'hidden' },
    photoCardDefault: { borderColor: '#bb86fc' },
    photoThumb: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', cursor: 'zoom-in', background: '#111' },
    photoThumbImg: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    defaultBadge: { position: 'absolute', top: '6px', left: '6px', background: '#bb86fc', color: '#121212', fontSize: '10px', fontWeight: 'bold', borderRadius: '4px', padding: '2px 6px' },
    privateBadge: { position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', fontSize: '12px' },
    photoCardBody: { padding: '8px' },
    photoCaption: { fontSize: '11px', color: '#aaa', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    setDefaultBtn: { width: '100%', background: 'transparent', border: '1px solid #bb86fc', borderRadius: '4px', color: '#bb86fc', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' },
    isDefaultLabel: { fontSize: '11px', color: '#bb86fc', textAlign: 'center', padding: '4px 0' },
    addPhotoBtn: { background: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
    lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 },
    lightboxCaption: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.7)', color: '#e0e0e0', padding: '8px 12px', fontSize: '13px', textAlign: 'center', borderRadius: '0 0 8px 8px' },
    lightboxClose: { position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', borderRadius: '4px', padding: '2px 8px' },

    // ── Stat value/label ──
    statValue: { fontSize: '36px', fontWeight: 'bold', color: '#bb86fc', marginBottom: '8px' },
    statLabel: { fontSize: '13px', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px' },

    // ── Section 3: Parent tabs ──
    tabsWrapper: { marginTop: '8px', width: '100%', minWidth: 0 },
    tabBar: { display: 'flex', boxShadow: 'inset 0 -2px 0 #333', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden', gap: '2px' },
    tabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '12px 20px', fontSize: '14px', fontWeight: '600', color: '#777', cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px' },
    tabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    tabContent: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '24px', minHeight: '200px', width: '100%', minWidth: 0, boxSizing: 'border-box' },

    // ── Sub-tabs (inside parent tab content) ──
    subTabBar: { display: 'flex', boxShadow: 'inset 0 -1px 0 #2e2e2e', gap: '2px', marginBottom: '0', overflowX: 'auto', overflowY: 'hidden' },
    subTabBtn: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '8px 16px', fontSize: '13px', fontWeight: '500', color: '#666', cursor: 'pointer', whiteSpace: 'nowrap' },
    subTabBtnActive: { color: '#bb86fc', borderBottom: '2px solid #bb86fc' },
    subTabContent: { paddingTop: '20px' },

    // ── Tables ──
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '11px 16px', color: '#e0e0e0', borderBottom: '1px solid #222' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    showCount: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px', fontSize: '13px', color: '#bb86fc', fontWeight: 'bold' },
    attendedBtnOff: { backgroundColor: 'transparent', color: '#666', border: '1px solid #444', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '4px', padding: '3px 10px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap' },

    // ── Pagination (jump-to-page) ──
    pageJumpWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' },
    pageJumpLabel: { fontSize: '13px', color: '#aaa', fontWeight: '600' },
    pageJumpSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '6px 10px', fontSize: '14px', color: '#e0e0e0', cursor: 'pointer', fontWeight: 'bold', width: '64px', flexShrink: 0, textAlign: 'center', textAlignLast: 'center' },

    tabSearch: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', marginBottom: '16px', boxSizing: 'border-box' },
    emptyTab: { textAlign: 'center', color: '#666', padding: '40px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.3) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
const Smobile = {
    // Image/meta (left) + info tabs (right) — stack instead of a fixed
    // 1fr/2fr grid.
    headerGrid: {
        gridTemplateColumns: '1fr',
        gap: '20px',
    },
    // "Total Shows" box + stats table (in the Show Stats tab) — stack
    // instead of a flex row, so the stats table isn't squeezed into ~200px.
    showStatsLayout: {
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 'auto',
    },
};

export default PromoterDetail;
